/**
 * Task Query Keys
 *
 * Centralized query key factory for all task-related queries.
 * Supports dashboard views (Today, Upcoming, All) and matter-specific tasks.
 */

import type { TaskFilters } from './types';

/**
 * Task query key factory
 *
 * @example
 * ```typescript
 * // Invalidate all task queries
 * queryClient.invalidateQueries({ queryKey: taskKeys.all })
 *
 * // Get today's tasks
 * useQuery({ queryKey: taskKeys.today({ userId: 1 }), queryFn: ... })
 *
 * // Invalidate matter-specific tasks
 * queryClient.invalidateQueries({ queryKey: taskKeys.forMatter(matterId) })
 * ```
 */
export const taskKeys = {
  /**
   * Root key for all task queries
   * Use for invalidating entire task cache
   */
  all: ['tasks'] as const,

  /**
   * Base key for task lists
   */
  lists: () => [...taskKeys.all, 'list'] as const,

  /**
   * Task list with filters
   * @param filters - Optional filters
   */
  list: (filters?: TaskFilters) => [...taskKeys.lists(), filters] as const,

  /**
   * Today's tasks (scheduled for today or past)
   * @param userId - Filter by assigned user
   * @param tags - Filter by matter tags
   * @param q - Search query
   */
  today: (userId?: number, tags?: string[], q?: string) =>
    [...taskKeys.all, 'today', userId, tags, q] as const,

  /**
   * Upcoming tasks (scheduled for future)
   * @param userId - Filter by assigned user
   * @param tags - Filter by matter tags
   * @param q - Search query
   */
  upcoming: (userId?: number, tags?: string[], q?: string) =>
    [...taskKeys.all, 'upcoming', userId, tags, q] as const,

  /**
   * All tasks with filters
   * @param filters - Optional filters
   */
  allTasks: (filters?: TaskFilters) =>
    [...taskKeys.all, 'all', filters] as const,

  /**
   * Base key for task details
   */
  details: () => [...taskKeys.all, 'detail'] as const,

  /**
   * Specific task detail
   * @param taskId - The task ID
   */
  detail: (taskId: number) => [...taskKeys.details(), taskId] as const,

  /**
   * Tasks for a specific matter
   * @param matterId - The matter ID
   */
  forMatter: (matterId: number) =>
    [...taskKeys.all, 'matter', matterId] as const,

  /**
   * Task dashboard data for a matter
   * @param matterId - The matter ID
   * @param format - Dashboard format (0 = grouped, 1 = aggregated)
   */
  dashboard: (matterId: number, format: 0 | 1 = 0) =>
    ['matter', matterId, format, 'tasks-dashboard-data'] as const,

  /**
   * Task dashboard (legacy format)
   * @param matterId - The matter ID
   */
  dashboardLegacy: (matterId: number) =>
    ['task-dashboard', String(matterId)] as const,

  /**
   * Task summary for a matter
   * @param matterId - The matter ID
   */
  summary: (matterId: number) =>
    [...taskKeys.forMatter(matterId), 'summary'] as const,

  /**
   * Institution-specific tasks
   * @param matterId - The matter ID
   * @param institutionId - The institution ID
   */
  forInstitution: (matterId: number, institutionId: number) =>
    ['institution-tasks', matterId, institutionId] as const,

  /**
   * Institution tasks dashboard
   * @param matterId - The matter ID
   * @param institutionId - The institution ID (optional)
   */
  institutionDashboard: (matterId: number, institutionId?: number) =>
    institutionId !== undefined
      ? ([
          'matter',
          matterId,
          'institution-tasks-dashboard',
          institutionId
        ] as const)
      : (['matter', matterId, 'institution-tasks-dashboard'] as const)
} as const;
